import { useState } from "react";
import {
  Button,
  Chip,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { i18n } from "@lingui/core";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import {
  getNotificationFailureExplanation,
  NotificationLogDetailModal,
  statusIcons,
} from "@/Components/AppointmentModal/components/NotificationLog";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { LimeLoader } from "@/Components/NextBase/LimeLoader";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { LimePagination } from "@/Components/NextBase/LimePagination";
import { channelIcons } from "@/constants/channelIcons";
import { api } from "@/lib/api-client";
import {
  buildSubscriptionNotificationRows,
  type SubscriptionNotificationRow,
} from "./subscriptionNotificationRows";

const PER_PAGE_OPTIONS = [5, 10];

function formatTimeSent(timeSent: Date | string): string {
  return new Date(timeSent).toLocaleString(i18n.locale, {
    dateStyle: "short",
    timeStyle: "short",
  });
}

export function CustomerSubscriptionNotificationsModal({
  isOpen,
  onClose,
  customerSubscriptionId,
}: {
  isOpen: boolean;
  onClose: () => void;
  customerSubscriptionId: number;
}) {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(PER_PAGE_OPTIONS[1]);
  const [selectedRow, setSelectedRow] =
    useState<SubscriptionNotificationRow | null>(null);

  const { data, isPending, isError, processedErrorMessage, refetch } =
    api.customer.useGetCustomerSubscriptionNotifications({
      customerSubscriptionId,
      query: { pagination: { page, perPage } },
      enabled: isOpen,
    });

  const rows = buildSubscriptionNotificationRows(data?.notifications ?? []);

  return (
    <>
      <LimeModal
        isOpen={isOpen}
        onClose={onClose}
        size="2xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader>
            <Trans>Obvestila paketa</Trans>
          </ModalHeader>
          <ModalBody>
            {isPending ? (
              <LimeLoader />
            ) : isError ? (
              <LimeAlert
                color="danger"
                data-identifier="subscription-notifications-error"
                message={
                  processedErrorMessage ??
                  t`Obvestil ni bilo mogoče pridobiti. Poskusite znova.`
                }
                endContent={
                  <Button
                    size="sm"
                    variant="flat"
                    color="danger"
                    onPress={() => refetch()}
                  >
                    <Trans>Poskusi znova</Trans>
                  </Button>
                }
              />
            ) : rows.length === 0 ? (
              <p
                className="text-default-500 text-sm"
                data-identifier="subscription-notifications-empty"
              >
                <Trans>Za ta paket še ni bilo poslanih obvestil.</Trans>
              </p>
            ) : (
              <div
                className="flex flex-col"
                data-identifier="subscription-notifications-list"
              >
                {rows.map((row) => (
                  <SubscriptionNotificationRowButton
                    key={row.key}
                    row={row}
                    onPress={() => setSelectedRow(row)}
                  />
                ))}
              </div>
            )}
            <div className="mt-2 flex w-full justify-center">
              <LimePagination
                paginationProps={{
                  page,
                  total: data?.pageCount,
                  onChange: setPage,
                }}
                perPageProps={{
                  label: t`obvestil`,
                  values: PER_PAGE_OPTIONS,
                  value: perPage.toString(),
                  onChange: (value) => {
                    setPerPage(Number(value));
                    setPage(1);
                  },
                }}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button onPress={onClose}>
              <Trans>Zapri</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </LimeModal>

      {selectedRow ? (
        <NotificationLogDetailModal
          isOpen
          onClose={() => setSelectedRow(null)}
          title={`${selectedRow.channel} - ${formatTimeSent(selectedRow.log.timeSent)}`}
          notificationLogId={selectedRow.log.notificationLogId}
        />
      ) : null}
    </>
  );
}

function SubscriptionNotificationRowButton({
  row,
  onPress,
}: {
  row: SubscriptionNotificationRow;
  onPress: () => void;
}) {
  const { log } = row;
  const failureExplanation = row.isFailed
    ? getNotificationFailureExplanation({
        status: log.status,
        status_message: log.status_message,
        message_content: log.message_content,
        requestId: null,
      })
    : null;

  return (
    <button
      type="button"
      className="flex w-full cursor-pointer flex-col gap-1 rounded-md px-1 py-2 text-left hover:bg-default-100 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
      data-identifier="subscription-notification-row"
      onClick={onPress}
    >
      <div className="flex min-w-0 items-center gap-2">
        <span
          aria-hidden="true"
          className="flex shrink-0 items-center [&_svg]:size-5"
        >
          {channelIcons[row.channel]}
        </span>
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-sm leading-5">{row.label}</span>
          {row.period ? (
            <span className="text-default-500 text-xs leading-4">
              {t`Obdobje: ${row.period}`}
            </span>
          ) : null}
          {failureExplanation ? (
            <span className="text-default-500 text-xs leading-4">
              {failureExplanation}
            </span>
          ) : null}
        </span>
      </div>

      <div className="flex shrink-0 items-center gap-2 pl-7 sm:pl-0">
        <span className="text-default-500 text-xs">
          {formatTimeSent(log.timeSent)}
        </span>
        {row.isFailed ? (
          <Chip
            size="sm"
            variant="flat"
            color={
              log.status_message === "MONTHLY_LIMIT_EXCEEDED"
                ? "warning"
                : "danger"
            }
          >
            {t`Ni poslano`}
          </Chip>
        ) : (
          statusIcons(log.deliveryStatus, row.channel)
        )}
      </div>
    </button>
  );
}
